import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiConfig } from '../config/environment';
import {
  LoginResponse,
  LoginCredentials,
  User,
  ApiLoginResponse,
} from '../types';
import { humanizeForeignKeyDeleteError } from '../utils/humanizeDbError';

// Configuração base da API
const API_BASE_URL = apiConfig.baseURL;

// Interface para dados de login (mantida para compatibilidade)
export interface LoginData {
  email: string;
  password: string;
}

// Classe para gerenciar a API
class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratar erros de resposta
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      error => {
        if (error.response?.status === 401) {
          // Não fazer logout se for alteração de senha - manter usuário logado
          const isChangePassword = error.config?.url?.includes('/usuario/change-password');
          // Não fazer logout se estiver na página de login (erro de credenciais)
          const isLoginPage = window.location.pathname === '/login' || error.config?.url?.includes('/login');
          // Não fazer logout se estiver na página de rastreamento (página pública)
          const isTrackingPage = window.location.pathname === '/rastreamento' || error.config?.url?.includes('/verify') || error.config?.url?.includes('/pacote/verificar-cliente');

          if (!isChangePassword && !isLoginPage && !isTrackingPage) {
            // Token expirado ou inválido
            console.log(
              'Token expirado ou inválido, redirecionando para login...'
            );
            this.clearAuthData();

            // Redirecionar para login apenas se não estiver já na página de login
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Método para fazer login
  async login(data: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.api.post<ApiLoginResponse>(
        '/api/v1/login',
        data
      );

      // Extrair o token da resposta
      const { authorisation, user } = response.data;

      if (authorisation?.token) {
        // Armazenar o token no localStorage
        localStorage.setItem('token', authorisation.token);

        // Armazenar dados do usuário
        localStorage.setItem('user', JSON.stringify(user));

        // Retornar dados do usuário (sem o token por segurança)
        return { user, refresh_token: response.data.refresh_token };
      } else {
        throw new Error('Token de autorização não encontrado na resposta');
      }
    } catch (error: any) {
      // Tratar especificamente erros 401/Unauthorized
      if (error.response?.status === 401 || error.response?.data?.status === 'error') {
        const message = error.response?.data?.message || 'Credenciais inválidas. Verifique seu email e senha.';
        throw new Error(message);
      }
      throw new Error(error.response?.data?.message || error.message || 'Erro ao fazer login');
    }
  }

  // Método para fazer logout
  async logout(): Promise<void> {
    try {
      await this.api.post('/api/v1/logout');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Sempre limpar dados locais
      this.clearAuthData();
    }
  }

  // Método para limpar dados de autenticação
  clearAuthData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('Dados de autenticação limpos do localStorage');
  }

  // Método para forçar logout e limpeza completa
  forceLogout(): void {
    this.clearAuthData();

    // Redirecionar para login se não estiver já lá
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // Método para verificar se o usuário está autenticado
  isAuthenticated(): boolean {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      // Verificar se existe token e usuário válido
      if (!token || !user) {
        return false;
      }

      // Verificar se o usuário é válido
      const parsedUser = JSON.parse(user);
      if (!parsedUser || !parsedUser.id || !parsedUser.email) {
        // Limpar dados inválidos
        this.clearAuthData();
        return false;
      }

      // Retornar true se há token e usuário válido
      return true;
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      // Em caso de erro, limpar dados corrompidos
      this.clearAuthData();
      return false;
    }
  }

  // Método para verificar autenticação real com a API
  async checkAuthenticationWithAPI(): Promise<boolean> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }

      // Tentar fazer uma requisição para verificar se o token ainda é válido
      const response = await this.api.get('/api/v1/profile');
      return response.status === 200;
    } catch (error) {
      // Se houver erro (API offline, token expirado, etc.), limpar dados e retornar false
      console.error('Erro ao verificar autenticação com API:', error);
      this.clearAuthData();
      return false;
    }
  }

  // Método para obter o token atual
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  // Método para decodificar JWT e obter a data de expiração
  getTokenExpiration(token: string): number | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => {
            const hex = c.charCodeAt(0).toString(16);
            const paddedHex = `00${hex}`.slice(-2);
            return `%${paddedHex}`;
          })
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      return decoded.exp ? decoded.exp * 1000 : null; // Converter para milissegundos
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  // Método para renovar o token
  async refreshToken(): Promise<{ token: string; user: any }> {
    try {
      const response = await this.api.post('/api/v1/refresh');
      const { authorisation, user } = response.data;

      if (authorisation?.token) {
        // Atualizar o token no localStorage
        localStorage.setItem('token', authorisation.token);

        // Atualizar dados do usuário se fornecidos
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }

        return { token: authorisation.token, user };
      } else {
        throw new Error('Token de autorização não encontrado na resposta');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao renovar token');
    }
  }

  // Método para obter dados do usuário atual
  getCurrentUser(): User | null {
    try {
      const user = localStorage.getItem('user');
      if (!user) return null;

      const parsedUser = JSON.parse(user);

      // Verificar se o usuário tem as propriedades necessárias
      if (
        parsedUser &&
        typeof parsedUser === 'object' &&
        parsedUser.id &&
        parsedUser.email
      ) {
        return parsedUser;
      }

      // Se o usuário não for válido, limpar o localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return null;
    } catch (error) {
      console.error('Erro ao obter usuário atual:', error);
      // Em caso de erro, limpar dados corrompidos
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return null;
    }
  }

  // Método para buscar perfil do usuário da API
  async getProfile(): Promise<User> {
    try {
      console.log('🔍 Fazendo requisição para /api/v1/profile...');
      const response = await this.api.get('/api/v1/profile');

      // A API retorna { status, message, data }
      // Se response.data tem a propriedade 'data', usar ela, senão usar response.data diretamente
      const user = response.data?.data || response.data;

      console.log('📊 Resposta da API:', response);
      console.log('👤 Dados do usuário recebidos:', user);
      console.log('🔑 Estrutura permissao:', user.permissao);
      console.log('🔑 Permissão ID recebido:', user.permissao?.id);
      console.log('🔑 Permissão permission:', user.permissao?.permission);

      // Atualizar dados no localStorage
      localStorage.setItem('user', JSON.stringify(user));

      return user;
    } catch (error: any) {
      console.error('❌ Erro na API getProfile:', error);
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar perfil do usuário'
      );
    }
  }

  // Método para fazer requisições autenticadas
  async authenticatedRequest<T>(config: any): Promise<T> {
    try {
      const response = await this.api.request<T>(config);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro na requisição');
    }
  }

  // Método para solicitar recuperação de senha
  async forgotPassword(email: string): Promise<void> {
    try {
      await this.api.post('/api/v1/security/esquecer-senha', { email });
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao solicitar recuperação de senha'
      );
    }
  }

  // Método para verificar código de recuperação
  async checkRecoveryCode(email: string, code: string): Promise<string> {
    try {
      const response = await this.api.post('/api/v1/security/check-code', {
        email,
        code,
      });

      // Retornar o token gerado pela validação do código
      const token = response.data?.token || response.data?.authorisation?.token;

      if (!token) {
        throw new Error('Token não encontrado na resposta do servidor');
      }

      return token;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Código de recuperação inválido'
      );
    }
  }

    // Método para redefinir senha
  async resetPassword(
    email: string,
    code: string,
    token: string,
    password: string
  ): Promise<void> {
    try {
      console.log('Enviando requisição para resetPassword:', {
        email,
        code,
        token: '***',
        password: '***',
      });

      const response = await this.api.post('/api/v1/security/nova-senha', {
        email,
        code,
        token,
        password,
      });

      console.log('Resposta do servidor:', response.data);
    } catch (error: any) {
      console.error('Erro completo na API:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);

      let errorMessage = 'Erro ao redefinir senha';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos enviados para o servidor';
      } else if (error.response?.status === 401) {
        errorMessage = 'Código de verificação inválido ou expirado';
      } else if (error.response?.status === 404) {
        errorMessage = 'Serviço de recuperação não encontrado';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor';
      }

      throw new Error(errorMessage);
    }
  }

  // Método para atualizar perfil
  async updateProfile(data: {
    nome?: string;
    telefone?: string;
    endereco?: string;
  }): Promise<User> {
    try {
      const response = await this.api.put('/api/v1/profile', data);
      // A API pode retornar { status, message, data } ou diretamente o user
      const user = response.data?.data || response.data;
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar perfil'
      );
    }
  }

  async updateAvatar(avatar: File): Promise<User> {
    try {
      const formData = new FormData();
      formData.append('avatar', avatar);

      const response = await this.api.post('/api/v1/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // A API pode retornar { status, message, data } ou diretamente o user
      const userData = response.data?.data || response.data;

      // Atualizar dados no localStorage
      localStorage.setItem('user', JSON.stringify(userData));

      return userData;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar avatar'
      );
    }
  }

  async getProfileStats(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/profile/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar estatísticas do perfil'
      );
    }
  }

  async changePassword(data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/usuario/change-password', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao alterar senha'
      );
    }
  }

  // ========== DASHBOARD ==========
  async getDashboardSummary(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/dashboard/summary');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar resumo do dashboard'
      );
    }
  }

  async getClients(params?: {
    search?: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/clients', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar clientes'
      );
    }
  }

  async getInvoices(params?: { status?: string }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/invoices', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar invoices'
      );
    }
  }

  async getLocations(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/locations');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar localizações'
      );
    }
  }

  // ========== CLIENTES ==========
  async getClientsList(params?: {
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/clients', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar clientes'
      );
    }
  }

  async createClient(data: {
    nome: string;
    email: string;
    telefone?: string;
    documento?: string;
    endereco?: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/clients', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar cliente');
    }
  }

  async updateClient(
    id: number,
    data: Partial<{
      nome: string;
      email: string;
      telefone: string;
      documento: string;
      endereco: string;
      status: string;
    }>
  ): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/clients/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar cliente'
      );
    }
  }

  async deleteClient(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/clients/${id}`);
    } catch (error: any) {
      const raw =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        '';
      const message = humanizeForeignKeyDeleteError(
        raw || 'Erro ao excluir cliente',
        'este cliente'
      );
      throw new Error(message);
    }
  }

  // ========== USUÁRIOS ==========
  async getUsers(params?: {
    search?: string;
    status?: string;
    role?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/usuario/listar', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar usuários'
      );
    }
  }

  async createUser(data: {
    nome: string;
    email: string;
    password: string;
    telefone?: string;
    permissaos_id: number;
    department?: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/usuario/cadastro', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar usuário');
    }
  }

  async updateUser(
    id: number,
    data: Partial<{
      nome: string;
      email: string;
      telefone?: string;
      permissaos_id: number;
      department: string;
      status: string;
    }>
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/users/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar usuário'
      );
    }
  }

  async deleteUser(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/users/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir usuário'
      );
    }
  }

  // ========== PERMISSÕES ==========
  async getPermissaos(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/permissaos');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar permissões'
      );
    }
  }

  async createPermissao(data: {
    permissao: string;
    nome: string;
    descricao?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/permissaos', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar permissão'
      );
    }
  }

  async updatePermissao(
    id: number,
    data: {
      permissao: string;
      nome: string;
      descricao?: string;
    }
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/permissaos/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar permissão'
      );
    }
  }

  async deletePermissao(id: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/permissaos/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir permissão'
      );
    }
  }

  // ========== PACOTES ==========
  async getPackages(params?: {
    search?: string;
    status?: string;
    year?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/packages', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar pacotes'
      );
    }
  }

  async createPackage(data: any): Promise<any> {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'items') {
          // Enviar items como JSON string
          formData.append(key, JSON.stringify(data[key]));
        } else if (key === 'purchaseInvoice' && data[key]) {
          // Se for File, enviar diretamente
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          }
        } else if (key === 'fotos' && Array.isArray(data[key])) {
          // Enviar múltiplas fotos
          data[key].forEach((foto: File | string, index: number) => {
            if (foto instanceof File) {
              formData.append(`fotos[${index}]`, foto);
            } else if (typeof foto === 'string') {
              // Se for base64, enviar como string
              formData.append(`fotos[${index}]`, foto);
            }
          });
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string (apenas cliente agora)
          // Formato esperado: { cliente: "CODIGO123" }
          if (typeof data[key] === 'object') {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        } else if (
          data[key] !== null &&
          data[key] !== undefined &&
          data[key] !== ''
        ) {
          // Converter números para string
          if (typeof data[key] === 'number') {
            formData.append(key, String(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      const response = await this.api.post('/api/v1/packages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar pacote');
    }
  }

  async updatePackage(id: number, data: any): Promise<any> {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'items') {
          // Enviar items como JSON string
          formData.append(key, JSON.stringify(data[key]));
        } else if (key === 'purchaseInvoice' && data[key]) {
          // Se for File, enviar diretamente
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          }
        } else if (key === 'fotos' && Array.isArray(data[key])) {
          // Enviar múltiplas fotos
          data[key].forEach((foto: File | string, index: number) => {
            if (foto instanceof File) {
              formData.append(`fotos[${index}]`, foto);
            } else if (typeof foto === 'string') {
              // Se for base64, enviar como string
              formData.append(`fotos[${index}]`, foto);
            }
          });
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string (apenas cliente agora)
          // Formato esperado: { cliente: "CODIGO123" }
          if (typeof data[key] === 'object') {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        } else if (
          data[key] !== null &&
          data[key] !== undefined &&
          data[key] !== ''
        ) {
          // Converter números para string
          if (typeof data[key] === 'number') {
            formData.append(key, String(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      const response = await this.api.patch(
        `/api/v1/packages/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar pacote'
      );
    }
  }

  async deletePackage(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/packages/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir pacote'
      );
    }
  }

  async getPackage(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/packages/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar pacote');
    }
  }

  // ========== INVOICES ==========
  async getInvoicesList(params?: {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/invoices', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar invoices'
      );
    }
  }

  async createInvoice(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/invoices', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar invoice');
    }
  }

  async updateInvoice(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/invoices/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar invoice'
      );
    }
  }

  async deleteInvoice(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/invoices/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir invoice'
      );
    }
  }

  async getInvoiceStatus(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/invoice/status');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar status de invoice'
      );
    }
  }

  // ========== FORNECEDORES ==========
  async getSuppliers(params?: {
    search?: string;
    status?: string;
    category?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/suppliers', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar fornecedores'
      );
    }
  }

  // Endpoint específico para listar fornecedores (usado em invoices)
  async getFornecedor(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/fornecedor');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar fornecedores'
      );
    }
  }

  async createSupplier(data: {
    razao_social: string;
    nome_fantasia?: string;
    email: string;
    telefone?: string;
    cnpj?: string;
    endereco?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
    bairro?: string;
    numero?: string;
    complemento?: string;
    categoria: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/suppliers', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar fornecedor'
      );
    }
  }

  async updateSupplier(
    id: number,
    data: Partial<{
      razao_social: string;
      nome_fantasia?: string;
      email: string;
      telefone: string;
      cnpj?: string;
      endereco?: string;
      cidade?: string;
      uf?: string;
      cep?: string;
      bairro?: string;
      numero?: string;
      complemento?: string;
      categoria: string;
      status: string;
    }>
  ): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/suppliers/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar fornecedor'
      );
    }
  }

  async deleteSupplier(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/suppliers/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir fornecedor'
      );
    }
  }

  // ========== CAIXAS E MALAS ==========
  async getBoxes(params?: {
    search?: string;
    tipo?: string;
    status?: string;
    page?: number;
    per_page?: number;
    /** Código da invoice (ex.: INV-2024-001) — backend pode mapear para codigo_invoice */
    codigo_invoice?: string;
    destino?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/packages', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar caixas/malas'
      );
    }
  }

  async createBox(data: any): Promise<any> {
    try {
      const formData = new FormData();

      // Processar itens: enviar dados como JSON e fotos como binários separados
      if (data.itens && Array.isArray(data.itens) && data.itens.length > 0) {
        // Preparar array de itens sem as fotos (apenas dados)
        const itensData = data.itens.map((item: any) => {
          const itemData: any = {};
          if (item.nome && item.nome.trim() !== '') {
            itemData.nome = String(item.nome);
          }
          if (
            item.quantidade !== undefined &&
            item.quantidade !== null &&
            item.quantidade !== ''
          ) {
            itemData.quantidade = String(item.quantidade);
          }
          if (
            item.peso !== undefined &&
            item.peso !== null &&
            item.peso !== ''
          ) {
            itemData.peso = String(item.peso);
          }
          if (
            item.preco !== undefined &&
            item.preco !== null &&
            item.preco !== ''
          ) {
            itemData.preco = String(item.preco);
          }
          return itemData;
        });

        // Enviar dados dos itens como JSON string
        formData.append('itens', JSON.stringify(itensData));

        // Enviar fotos dos itens como arquivos binários separados
        // Formato: itens_fotos[0][0], itens_fotos[0][1], itens_fotos[1][0], etc.
        data.itens.forEach((item: any, itemIndex: number) => {
          if (
            item.fotos &&
            Array.isArray(item.fotos) &&
            item.fotos.length > 0
          ) {
            let photoIndex = 0;
            item.fotos.forEach((photo: any) => {
              // Apenas enviar arquivos novos (File), não URLs existentes
              if (photo?.file instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo.file
                );
                photoIndex++;
              } else if (photo instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo
                );
                photoIndex++;
              }
            });
          }
        });
      }

      // Processar outros campos - TODOS devem ser enviados (conforme especificação)
      Object.keys(data).forEach(key => {
        if (key === 'itens') {
          // Já processado acima, pular
          return;
        } else if (key === 'dimensoes') {
          // No CREATE, dimensões podem ser enviadas como campos separados OU como JSON string (alternativa)
          // Se vier como objeto dimensoes, enviar como JSON string também
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string' && data[key].trim() !== '') {
            formData.append(key, data[key]);
          }
        } else if (
          key === 'comprimento' ||
          key === 'largura' ||
          key === 'altura'
        ) {
          // Dimensões como campos separados - enviar sempre (mesmo se vazio)
          if (
            data[key] !== null &&
            data[key] !== undefined &&
            data[key] !== ''
          ) {
            formData.append(key, String(data[key]));
          } else {
            formData.append(key, '');
          }
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string' && data[key].trim() !== '') {
            formData.append(key, data[key]);
          }
        } else if (key === 'nota_fiscal') {
          // nota_fiscal - enviar arquivo(s) com o mesmo nome (sem array)
          // Apenas enviar se existir e for File
          if (data[key]) {
            if (Array.isArray(data[key])) {
              data[key].forEach((file: any) => {
                if (file?.file instanceof File) {
                  formData.append('nota_fiscal', file.file);
                } else if (file instanceof File) {
                  formData.append('nota_fiscal', file);
                }
              });
            } else if (data[key]?.file instanceof File) {
              formData.append('nota_fiscal', data[key].file);
            } else if (data[key] instanceof File) {
              formData.append('nota_fiscal', data[key]);
            }
          }
        } else {
          // Enviar TODOS os outros campos sempre (mesmo opcionais)
          // Converter null/undefined para string vazia quando necessário
          const value = data[key];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, String(value));
          } else {
            // Para campos opcionais vazios, enviar string vazia
            formData.append(key, '');
          }
        }
      });

      const response = await this.api.post('/api/v1/packages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar caixa/mala'
      );
    }
  }

  async updateBox(id: number, data: any): Promise<any> {
    try {
      const formData = new FormData();

      // Processar itens: enviar dados como JSON e fotos como binários separados
      if (data.itens && Array.isArray(data.itens) && data.itens.length > 0) {
        // Preparar array de itens sem as fotos (apenas dados)
        const itensData = data.itens.map((item: any) => {
          const itemData: any = {};

          // Incluir ID do item se existir (para atualização, não duplicar)
          if (item.id !== undefined && item.id !== null && item.id !== '') {
            itemData.id = String(item.id);
          }

          if (item.nome && item.nome.trim() !== '') {
            itemData.nome = String(item.nome);
          }
          if (item.ncm && item.ncm.trim() !== '') {
            itemData.ncm = String(item.ncm);
          }
          if (item.descricao && item.descricao.trim() !== '') {
            itemData.descricao = String(item.descricao);
          }
          if (
            item.quantidade !== undefined &&
            item.quantidade !== null &&
            item.quantidade !== ''
          ) {
            itemData.quantidade = String(item.quantidade);
          }
          if (
            item.peso !== undefined &&
            item.peso !== null &&
            item.peso !== ''
          ) {
            itemData.peso = String(item.peso);
          }
          if (
            item.preco !== undefined &&
            item.preco !== null &&
            item.preco !== ''
          ) {
            itemData.preco = String(item.preco);
          }
          return itemData;
        });

        // Enviar dados dos itens como JSON string
        formData.append('itens', JSON.stringify(itensData));

        // Enviar fotos dos itens como arquivos binários separados
        // Formato: itens_fotos[0][0], itens_fotos[0][1], itens_fotos[1][0], etc.
        data.itens.forEach((item: any, itemIndex: number) => {
          if (
            item.fotos &&
            Array.isArray(item.fotos) &&
            item.fotos.length > 0
          ) {
            let photoIndex = 0;
            item.fotos.forEach((photo: any) => {
              // Apenas enviar arquivos novos (File), não URLs existentes
              if (photo?.file instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo.file
                );
                photoIndex++;
              } else if (photo instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo
                );
                photoIndex++;
              }
              // Se for URL string (foto já existente), não enviar novamente
            });
          }
        });
      }

      // Processar outros campos - TODOS os campos devem ser enviados (conforme especificação)
      Object.keys(data).forEach(key => {
        if (key === 'itens') {
          // Já processado acima, pular
          return;
        } else if (key === 'dimensoes') {
          // No UPDATE, dimensões podem ser enviadas como campos separados OU como JSON string
          // Se vier como objeto dimensoes, enviar como JSON string (conforme especificação)
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string') {
            // Se já for string (JSON), enviar diretamente
            formData.append(key, data[key]);
          }
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string') {
            // Se já for string (JSON), enviar diretamente
            formData.append(key, data[key]);
          }
        } else if (key === 'nota_fiscal') {
          // nota_fiscal - enviar arquivo(s) com o mesmo nome (sem array)
          // Apenas enviar se existir e for File
          if (data[key]) {
            if (Array.isArray(data[key])) {
              data[key].forEach((file: any) => {
                // Apenas enviar arquivos novos (File), não URLs existentes
                if (file?.file instanceof File) {
                  formData.append('nota_fiscal', file.file);
                } else if (file instanceof File) {
                  formData.append('nota_fiscal', file);
                }
              });
            } else if (data[key]?.file instanceof File) {
              formData.append('nota_fiscal', data[key].file);
            } else if (data[key] instanceof File) {
              formData.append('nota_fiscal', data[key]);
            }
          }
        } else {
          // Enviar TODOS os outros campos sempre (mesmo opcionais)
          // Converter null/undefined para string vazia quando necessário
          const value = data[key];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, String(value));
          } else {
            // Para campos opcionais vazios, enviar string vazia
            formData.append(key, '');
          }
        }
      });

      const response = await this.api.put(`/api/v1/packages/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar caixa/mala'
      );
    }
  }

  async deleteBox(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/packages/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir caixa/mala'
      );
    }
  }

  // ========== COLETAS (recurso dedicado; não usar /api/v1/packages) ==========
  async getColetas(params?: {
    search?: string;
    page?: number;
    per_page?: number;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/coletas', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar coletas'
      );
    }
  }

  async createColeta(data: Record<string, unknown>): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/coletas', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar coleta'
      );
    }
  }

  async updateColeta(
    id: number | string,
    data: Record<string, unknown>
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/coletas/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar coleta'
      );
    }
  }

  async deleteColeta(id: number | string): Promise<void> {
    try {
      await this.api.delete(`/api/v1/coletas/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir coleta'
      );
    }
  }

  async getBoxCodes(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/packages/${id}/codes`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar códigos de acesso'
      );
    }
  }

  // ========== DOCUMENTOS ==========
  async getDocuments(params?: {
    search?: string;
    tipo?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/documents', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar documentos'
      );
    }
  }

  async createDocument(data: {
    tipo: string;
    descricao: string;
    arquivo: File;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('tipo', data.tipo);
      formData.append('descricao', data.descricao);
      formData.append('arquivo', data.arquivo);

      const response = await this.api.post('/api/v1/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar documento'
      );
    }
  }

  async getDocument(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/documents/${id}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar documento'
      );
    }
  }

  async downloadDocument(id: number): Promise<Blob> {
    try {
      const response = await this.api.get(`/api/v1/documents/${id}/download`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao baixar documento'
      );
    }
  }

  async deleteDocument(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/documents/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir documento'
      );
    }
  }

  // ========== DOCUMENTAÇÃO ADUANEIRA ==========
  async getCustoms(params?: {
    search?: string;
    box_id?: number;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/customs', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar documentação aduaneira'
      );
    }
  }

  async createCustom(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/customs', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar documentação aduaneira'
      );
    }
  }

  async updateCustom(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/customs/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao atualizar documentação aduaneira'
      );
    }
  }

  async deleteCustom(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/customs/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao excluir documentação aduaneira'
      );
    }
  }

  // ========== ROTAS E TRANSPORTES ==========
  async getRoutes(params?: {
    search?: string;
    status?: string;
    tipo_transporte?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/routes', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar rotas');
    }
  }

  async createRoute(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/routes', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar rota');
    }
  }

  async updateRoute(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/routes/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar rota'
      );
    }
  }

  async deleteRoute(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/routes/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir rota');
    }
  }

  // ========== RASTREAMENTO ==========
  async getTracking(code: string): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/tracking/${code}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar rastreamento'
      );
    }
  }

  async addTrackingStep(code: string, data: any): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/tracking/${code}/steps`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao adicionar etapa de rastreamento'
      );
    }
  }

  async updateTrackingStep(stepId: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(
        `/api/v1/tracking/steps/${stepId}`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao atualizar etapa de rastreamento'
      );
    }
  }

  async deleteTrackingStep(stepId: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/tracking/steps/${stepId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir etapa de rastreamento'
      );
    }
  }

  // ========== CUSTOS ==========
  async getCosts(params?: {
    search?: string;
    categoria?: string;
    status?: string;
    page?: number;
    per_page?: number;
    data_inicio?: string;
    data_fim?: string;
    sort?: string;
    order?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/costs', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar custos');
    }
  }

  async createCost(data: any): Promise<any> {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'comprovante' && data[key]) {
          if (data[key].file) {
            formData.append('comprovante', data[key].file);
          }
        } else if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Adicionar metodo_pagamento se fornecido
      if (data.metodo_pagamento !== undefined && data.metodo_pagamento !== null) {
        formData.append('metodo_pagamento', String(data.metodo_pagamento));
      }

      const response = await this.api.post('/api/v1/costs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar custo');
    }
  }

  async cancelCost(id: number, justificativa: string): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/costs/${id}/cancel`, {
        justificativa_cancelamento: justificativa,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao cancelar custo'
      );
    }
  }

  async getCostsSummary(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/costs/summary');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar resumo de custos'
      );
    }
  }

  // ========== PRODUTOS ==========
  async getProducts(params?: {
    search?: string;
    categoria_id?: number;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/products', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar produtos'
      );
    }
  }

  async createProduct(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/products', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar produto');
    }
  }

  async updateProduct(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/products/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar produto'
      );
    }
  }

  async deleteProduct(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/products/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir produto'
      );
    }
  }

  async getProductCategories(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/products/categories');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar categorias de produtos'
      );
    }
  }

  // ========== PRODUTO (endpoints alternativos) ==========
  async addProduto(data: {
    nome: string;
    descricao: string;
    preco: number;
    categoria_id: number;
    foto?: string;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('descricao', data.descricao);
      formData.append('preco', String(data.preco));
      formData.append('categoria_id', String(data.categoria_id));
      if (data.foto) {
        formData.append('foto', data.foto);
      }

      const response = await this.api.post('/api/v1/produto/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao adicionar produto'
      );
    }
  }

  async listProdutos(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/produto/list');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao listar produtos'
      );
    }
  }

  // ========== CONFIGURAÇÕES ==========
  async getSettings(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/settings');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar configurações'
      );
    }
  }

  async updateSettings(data: any): Promise<any> {
    try {
      const response = await this.api.patch('/api/v1/settings', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar configurações'
      );
    }
  }

  async getRoles(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/settings/roles');
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao buscar roles';
      throw new Error(errorMessage);
    }
  }

  async createRole(data: {
    nome: string;
    descricao?: string;
    permissaos_id: number;
    paginaList: string[];
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/settings/roles', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar role');
    }
  }

  async updateRole(
    id: number,
    data: {
      nome: string;
      descricao?: string;
      permissaos_id: number;
      paginaList: string[];
    }
  ): Promise<any> {
    try {
      const response = await this.api.put(
        `/api/v1/settings/roles/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar role'
      );
    }
  }

  async deleteRole(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/settings/roles/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir role');
    }
  }

  // ========== ETIQUETAS ==========
  async getLabel(boxId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/labels/${boxId}`);
      return response.data;
    } catch (error: any) {
      // Preservar o erro original para que o componente possa tratá-lo adequadamente
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar etiqueta';
      const serverError: any = new Error(errorMessage);
      serverError.response = error.response;
      serverError.status = error.response?.status;
      serverError.isServerError = error.response?.status >= 500;
      throw serverError;
    }
  }

  async verifyCode(code: string): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/verify`, {
        params: { code },
      });

      // A API retorna { status: "success", data: {...} }
      // Precisamos adaptar para o formato esperado pelo componente { box: {...}, invoice: {...} }
      const apiData = response.data;

      if (apiData.status === 'success' && apiData.data) {
        const data = apiData.data;

        // Extrair status_invoice do objeto status se existir
        const statusInvoice = data.status?.status_invoice || data.status_invoice || data.status;
        const statusId = data.status?.id || null;

        // Mapear os dados da API para o formato esperado pelo componente
        return {
          box: {
            id: data.id,
            codigo: data.codigo || code,
            tipo: data.tipo?.toLowerCase() || 'caixa',
            status: typeof statusInvoice === 'string' ? statusInvoice : (data.status?.status_invoice || 'pedido_criado'),
            peso: data.peso || data.peso_total,
            codigo_rastreio: data.codigo_rastreio || data.codigo_rastreamento,
            data_criacao: data.data_criacao,
            // Adicionar campos que podem não estar na resposta mas são esperados
            cliente_nome: data.cliente_destinatario || data.cliente_nome || 'Não informado',
            dimensoes: data.dimensoes,
            valor_remessa: data.valor_remessa,
            conteudo: data.conteudo,
            observacao: data.observacao,
            historico_status: data.historico_status || [],
          },
          invoice: {
            numero_documento: data.numero_documento,
            codigo_rastreamento: data.codigo_rastreio || data.codigo_rastreamento,
            origem: data.origem,
            destino: data.destino,
            data: data.data_criacao,
            status_id: statusId,
            status_invoice: typeof statusInvoice === 'string' ? statusInvoice : (statusInvoice?.status_invoice || statusInvoice?.nome || statusInvoice?.name || ''),
            cliente_destinatario: data.cliente_destinatario,
            peso_total: data.peso_total,
            codigos_acesso: data.codigos_acesso,
          },
        };
      }

      // Fallback para formato antigo (se a API retornar diretamente { box: {...} })
      return apiData;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao verificar código'
      );
    }
  }

  // Método para verificar código de acesso
  async verifyAccess(data: {
    numero_documento: string;
    codigo_acesso: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/verify/access', data);

      // A resposta deve trazer os pacotes de cada cliente, com fotos e detalhes
      // Formato esperado: { status: "success", data: { pacotes: [...], clientes: [...] } }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao verificar código de acesso'
      );
    }
  }

  // Método para reportar ocorrência do pacote (chegou/extraviado/violado)
  async reportPackageOccurrence(data: {
    pacote_id: number;
    numero_documento: string;
    codigo_acesso: string;
    tipo: 'chegou' | 'extraviado' | 'violado';
    descricao?: string;
    fotos?: (File | string)[]; // Arquivos ou base64
  }): Promise<any> {
    try {
      const formData = new FormData();

      formData.append('pacote_id', String(data.pacote_id));
      formData.append('numero_documento', data.numero_documento);
      formData.append('codigo_acesso', data.codigo_acesso);
      formData.append('tipo', data.tipo);

      if (data.descricao) {
        formData.append('descricao', data.descricao);
      }

      // Adicionar fotos se fornecidas
      if (data.fotos && Array.isArray(data.fotos)) {
        data.fotos.forEach((foto: File | string, index: number) => {
          if (foto instanceof File) {
            formData.append(`fotos[${index}]`, foto);
          } else if (typeof foto === 'string') {
            // Se for base64, enviar como string
            formData.append(`fotos[${index}]`, foto);
          }
        });
      }

      const response = await this.api.post('/api/v1/pacote/reportar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao reportar ocorrência do pacote'
      );
    }
  }

  // Método para verificar pacotes do cliente
  async verifyClientPackages(data: {
    codigo_cliente: string;
    codigo_acesso: string;
    numero_documento: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/pacote/verificar-cliente', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao verificar pacotes do cliente'
      );
    }
  }

  // Método para reportar retirada do pacote
  async reportPackageWithdrawal(data: {
    codigo_cliente: string;
    numero_documento: string;
    codigo_acesso: string;
    foto_saida: File | string;
    foto_assinatura?: File | string;
    foto_documento: File | string;
  }): Promise<any> {
    try {
      const formData = new FormData();

      formData.append('codigo_cliente', data.codigo_cliente);
      formData.append('numero_documento', data.numero_documento);
      formData.append('codigo_acesso', data.codigo_acesso);

      // Adicionar fotos
      if (data.foto_saida instanceof File) {
        formData.append('foto_saida', data.foto_saida);
      } else {
        formData.append('foto_saida', data.foto_saida);
      }

      // foto_assinatura é opcional
      if (data.foto_assinatura) {
        if (data.foto_assinatura instanceof File) {
          formData.append('foto_assinatura', data.foto_assinatura);
        } else {
          formData.append('foto_assinatura', data.foto_assinatura);
        }
      }

      if (data.foto_documento instanceof File) {
        formData.append('foto_documento', data.foto_documento);
      } else {
        formData.append('foto_documento', data.foto_documento);
      }

      const response = await this.api.post('/api/v1/pacote/reportar-retirada', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao reportar retirada do pacote'
      );
    }
  }

  // ========== PAGAMENTOS ==========
  async createPayment(data: {
    pacote_id: number;
    valor?: number | string;
    metodo_pagamento_id?: number;
    metodo_pagamento?: string;
    moeda_id?: number;
    comprovante?: File;
  }): Promise<any> {
    try {
      const formData = new FormData();

      formData.append('pacote_id', String(data.pacote_id));

      if (data.valor !== undefined && data.valor !== null) {
        formData.append('valor', String(data.valor));
      }

      if (data.metodo_pagamento_id !== undefined && data.metodo_pagamento_id !== null) {
        formData.append('metodo_pagamento_id', String(data.metodo_pagamento_id));
      }

      // Adicionar metodo_pagamento se fornecido
      if (data.metodo_pagamento !== undefined && data.metodo_pagamento !== null) {
        formData.append('metodo_pagamento', String(data.metodo_pagamento));
      }

      if (data.moeda_id !== undefined && data.moeda_id !== null) {
        formData.append('moeda_id', String(data.moeda_id));
      }

      if (data.comprovante && data.comprovante instanceof File) {
        formData.append('comprovante', data.comprovante);
      }

      // Adicionar tipo como "Entrada" para todos os registros de pagamento
      formData.append('tipo', 'Entrada');

      const response = await this.api.post('/api/v1/pagamento/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao registrar pagamento'
      );
    }
  }

  async getPaymentByPackage(pacoteId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/pagamento/pacote/${pacoteId}`);
      return response.data;
    } catch (error: any) {
      // Se não encontrar pagamento, não é erro crítico
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar pagamento do pacote'
      );
    }
  }

  async cancelPayment(data: {
    pacote_id: number;
    justificativa_cancelamento: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/pagamento/cancel', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao cancelar pagamento'
      );
    }
  }

  async getTrackingHistory(rastreioId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/rastreio/historico/${rastreioId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar histórico de rastreio'
      );
    }
  }

  async createTrackingHistory(data: {
    invoice_id: number;
    status?: string;
    descricao?: string;
    local_pacote?: string;
    localizacao?: string;
    latitude?: number;
    longitude?: number;
    evento_em?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/rastreio/historico', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar histórico de rastreio'
      );
    }
  }

  async updateTrackingHistory(id: number, data: {
    invoice_id?: number;
    status?: string;
    descricao?: string;
    local_pacote?: string;
    localizacao?: string;
    latitude?: number;
    longitude?: number;
    evento_em?: string;
  }): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/rastreio/historico/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar histórico de rastreio'
      );
    }
  }

  async deleteTrackingHistory(id: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/rastreio/historico/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao remover histórico de rastreio'
      );
    }
  }

  // ========== TARIFAS ==========
  async getTarifas(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/tarifas');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar tarifas'
      );
    }
  }

  async getTarifa(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/tarifas/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar tarifa'
      );
    }
  }

  async createTarifa(data: {
    nome: string;
    origem: string;
    destino: string;
    valor: number;
    kg_max: number;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/tarifas', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar tarifa'
      );
    }
  }

  async updateTarifa(
    id: number,
    data: {
      nome?: string;
      origem?: string;
      destino?: string;
      valor?: number;
      kg_max?: number;
    }
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/tarifas/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar tarifa'
      );
    }
  }

  async deleteTarifa(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/tarifas/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir tarifa'
      );
    }
  }
}

// Instância única do serviço
export const apiService = new ApiService();

export default apiService;
